import {
  skipToken,
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useI18nContext } from '@/i18n/i18n-react';
import type { PredefinedRange } from '@/hooks/use-metrics-date-filter';
import {
  computeDelta,
  previousRange,
  type DateRange,
  type DeltaView,
} from '@/lib/period-comparison';

/** Query options for one range; `enabled` applies to both periods. */
export type RangeQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error, TData, QueryKey>,
  'enabled'
> & { enabled?: boolean };

export type WithPrevious<TData> = {
  current: UseQueryResult<TData, Error>;
  previous: UseQueryResult<TData, Error>;
  /** Variation of `valueOf` against the previous period (CMP-01/CMP-02). */
  delta: (valueOf: (data: TData) => number) => DeltaView;
};

/** Key of the never-run previous query when the range has invalid dates. */
const NO_PREVIOUS_KEY = ['with-previous', 'unavailable'];

/**
 * Runs the same query for the current range and for {@link previousRange}
 * (distinct keys, so the cache is shared with any other consumer of the same
 * range). The delta is `none` while either is missing or when the previous
 * one fails, and `noBase` when the previous value is 0.
 */
// SPEC_DEVIATION: the design's signature is `useWithPrevious(queryOptionsFactory,
// range)` without `enabled`; here `enabled` comes from the factory's options.
// Reason: the same gate (e.g. `analyticsEnabled`) must hold for both periods.
export function useWithPrevious<TData>(
  queryOptionsFor: (range: DateRange) => RangeQueryOptions<TData>,
  range: DateRange & { preset?: PredefinedRange }
): WithPrevious<TData> {
  const { locale } = useI18nContext();
  const prevRange = previousRange(range);

  const currentOptions = queryOptionsFor({ from: range.from, to: range.to });
  const current = useQuery({
    ...currentOptions,
    enabled: currentOptions.enabled ?? true,
  });

  const previousOptions = prevRange ? queryOptionsFor(prevRange) : null;
  const previous = useQuery<TData, Error, TData, QueryKey>(
    previousOptions
      ? { ...previousOptions, enabled: previousOptions.enabled ?? true }
      : { queryKey: NO_PREVIOUS_KEY, queryFn: skipToken }
  );

  const delta = (valueOf: (data: TData) => number): DeltaView => {
    if (current.data === undefined) return { kind: 'none' };
    if (previous.isError || previous.data === undefined) {
      return { kind: 'none' };
    }
    return computeDelta(valueOf(current.data), valueOf(previous.data), locale);
  };

  return { current, previous, delta };
}
